import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '@/lib/toast';
import type { JSX } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useDialogState } from '@/hooks/useDialogState';
import { LoadingState } from '@/components';
import type { IconName } from '@/components/ui/icon';
import { PageHeader, PageLayout, PageContent, TabNavigation } from '@/components/blocks/page';
import { useBreadcrumbEffect, type BreadcrumbItem } from '@/components/blocks/chrome';
import { useCompany, useContextActivities, useCompleteAction, useStartActivity } from '../../hooks';
import { useCompanyReports } from '../../../reports/hooks';
import { useEntityDevLogger } from '@/hooks/useEntityDevLogger';
import { usePrefetchCompanyData } from '@/hooks/usePrefetchCompanyData';
import { formatSiteShorthand, resolveSiteDisplayLabel } from '@/utils/entityIdentity';
import { addRecentItem } from '@/hooks/useRecentItems';
import { formatIsoDate, formatIsoDateItShort } from '@/utils/date';
import { resolveUnitsPlanningSnapshot } from '@/utils/inspectionPlanning';
import { deriveScheduleState, resolvePlannedVisitAt } from '@/utils/inspectionScheduling';

import type { ReportSummary } from '@/data/repos/reportsRepo';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDataProvider } from '@/data/provider';
import { queryKeys } from '@/lib/query-keys';
import type { PersonRecord } from '@/data/schemas';
import { EditCompanyModal } from './EditCompanyModal';
import { NewSiteModal } from './NewSiteModal';
import { PersonnelModal } from './PersonnelModal';
import { NewPersonnelModal } from './NewPersonnelModal';
import {
  describeMonthWindow,
  formatFrequencyLabel,
  frequencySummaryLabel,
} from '../../../../utils/periodicity';
import { groupPersonnelByScope, sortPersonnelByPriority } from '@/data/personnel/personnelScope';
import { formatDate, parseDate } from './companyDetailDates';
import type { PersonnelGroups, SiteSummary } from './types';
import { CompanyLogoMark } from '../../components/CompanyLogoMark';
import { CompanyOverviewTab, CompanyRegistrySection, type ActivityItem } from './tabs/OverviewTab';
import { CompanyPersonnelTab } from './tabs/PersonnelTab';
import { CompanySitesTab } from './tabs/SitesTab';
import { PlanningDateDialog } from '../../components/PlanningDateDialog';
import { getDateStatus } from '@/utils/dateStatus';

const companyTabs = ['operativita', 'anagrafica'] as const;
type CompanyTab = (typeof companyTabs)[number];

function deriveSiteSummaries(
  company: ReturnType<typeof useCompany>['data'],
  reports: ReportSummary[]
): SiteSummary[] {
  if (!company) return [];
  const now = Date.now();

  return company.sites.map((site) => {
    const relatedReports = reports.filter((report) => report.siteId === site.id);

    const refrigerationUnitsCount =
      (Array.isArray(site.refrigerationUnits) ? site.refrigerationUnits.length : undefined) ??
      site.refrigerationUnitsCount ??
      0;

    const planningSnapshot = resolveUnitsPlanningSnapshot(site.refrigerationUnits ?? []);

    const frequencyLabel = site.periodicity ? formatFrequencyLabel(site.periodicity) : '—';
    const frequencySummary = site.periodicity ? frequencySummaryLabel(site.periodicity) : '—';
    const frequencyMonths = site.periodicity ? parseInt(site.periodicity, 10) : 6;

    type TimedReport = { report: ReportSummary; time: number };
    const timedReports: TimedReport[] = relatedReports
      .map((report) => ({ report, time: parseDate(report.visitDate) }))
      .filter((entry): entry is TimedReport => typeof entry.time === 'number');

    const reportsByNewest = [...timedReports].sort((a, b) => b.time - a.time);
    const pastReports = reportsByNewest.filter((entry) => entry.time < now);
    const lastFinalized = [...pastReports]
      .filter((entry) => entry.report.status === 'finalized')
      .sort((a, b) => b.time - a.time)[0];

    const latestDraft = [...timedReports]
      .filter((entry) => entry.report.status === 'draft')
      .sort((a, b) => b.time - a.time)[0]?.report;

    const lastFinalizedIso =
      lastFinalized?.time !== undefined ? formatIsoDate(new Date(lastFinalized.time)) : undefined;
    const lastVisitIso = lastFinalizedIso ?? site.periodicityPreviousVisit ?? undefined;
    const plannedVisitAt = resolvePlannedVisitAt(site);
    const schedule = deriveScheduleState({
      contractActivationDate: site.contractActivationDate,
      periodicityMonths: site.periodicity ? parseInt(site.periodicity, 10) : undefined,
      lastFinalizedReportAt: lastFinalizedIso,
      nextPlannedVisitAt: plannedVisitAt,
      hasDraft: Boolean(latestDraft),
    });
    const expectedMonthIso = schedule.expectedMonth
      ? formatIsoDate(schedule.expectedMonth)
      : undefined;
    const nextVisitWindow = expectedMonthIso ? describeMonthWindow(expectedMonthIso) : undefined;
    const nextVisitLabel = plannedVisitAt
      ? formatIsoDateItShort(plannedVisitAt)
      : (schedule.dueMonthLabel ?? 'Da pianificare');

    const { name: resolvedName, shorthand } = resolveSiteDisplayLabel(company, site);
    return {
      id: site.id,
      name: resolvedName,
      sequenceNumber: site.sequenceNumber ?? 0,
      shorthand,
      city: site.sedeOperativaCity,
      province: site.sedeOperativaProvince,
      planningDate: planningSnapshot.plannedDate,
      planningPeriod: planningSnapshot.plannedPeriod,
      plannedVisitAt,
      nextVisit: plannedVisitAt ?? expectedMonthIso,
      nextVisitWindow,
      nextVisitLabel,
      dueMonthLabel: schedule.dueMonthLabel,
      scheduleState: schedule.state,
      planningLabel: planningSnapshot.label,
      planningIsExact: planningSnapshot.isExact,
      lastVisit: lastVisitIso,
      ncOpen: relatedReports.reduce((acc, report) => acc + report.nonConformitiesOpen, 0),
      totalReports: relatedReports.length,
      refrigerationUnitsCount,
      frequencyLabel,
      frequencySummary,
      frequencyMonths,
      draftReportId: latestDraft?.id ?? null,
    };
  });
}

export function CompanyDetailScreen(): JSX.Element {
  const { companies } = useDataProvider();
  const navigate = useNavigate({ from: '/companies/$companyId' });
  const params = useParams({ from: '/companies/$companyId' });
  const search = useSearch({ from: '/companies/$companyId' });
  const { data: company, isLoading } = useCompany(params.companyId);
  const { data: reports = [] } = useCompanyReports(params.companyId);
  const { activities } = useContextActivities({
    companyId: params.companyId,
    maxItems: 5,
    urgentOnly: false,
  });
  const startActivityMutation = useStartActivity();
  const completeActivityMutation = useCompleteAction(params.companyId);
  const [editCompanyOpen, setEditCompanyOpen] = useDialogState('editCompany');
  const [newSiteOpen, setNewSiteOpen] = useDialogState('newSite');
  const [selectedPersonnel, setSelectedPersonnel] = useState<PersonRecord | null>(null);
  const [personnelDialogOpen, setPersonnelDialogOpen] = useDialogState('personnelModal');
  const [newPersonnelOpen, setNewPersonnelOpen] = useDialogState('newPersonnel');
  const [planningDialog, setPlanningDialog] = useState<{
    open: boolean;
    siteId?: string;
    siteName?: string;
    plannedVisitAt?: string | null;
  }>({ open: false });
  const queryClient = useQueryClient();

  // Track visit in recent items
  useEffect(() => {
    if (company) {
      addRecentItem({
        id: company.id,
        type: 'company',
        label: company.name,
      });
    }
  }, [company]);

  const devPayload = useMemo(() => {
    if (!company) return null;
    return {
      company,
      reports,
      activities,
    };
  }, [activities, company, reports]);

  useEntityDevLogger('Company detail', devPayload);

  // Prefetch company-related data for offline use
  usePrefetchCompanyData(params.companyId);

  const rawTab = typeof search.tab === 'string' ? search.tab : undefined;
  const isTabId = (value: unknown): value is CompanyTab =>
    typeof value === 'string' && (companyTabs as readonly string[]).includes(value);
  const currentTab: CompanyTab = isTabId(rawTab) ? rawTab : 'operativita';

  const handleTabSelect = useCallback(
    (tabId: CompanyTab) => {
      navigate({
        to: '/companies/$companyId',
        params: { companyId: params.companyId },
        search: {
          ...search,
          tab: tabId === 'operativita' ? undefined : tabId,
        },
        replace: true,
      });
    },
    [navigate, params.companyId, search]
  );

  const personnelModalElement = useMemo(() => {
    if (!selectedPersonnel || !company) return null;
    return (
      <PersonnelModal
        personnel={selectedPersonnel}
        companyId={company.id}
        companyCode={company.code}
        sites={company.sites}
        open={personnelDialogOpen}
        onOpenChange={setPersonnelDialogOpen}
      />
    );
  }, [personnelDialogOpen, selectedPersonnel, company, setPersonnelDialogOpen]);

  const siteSummaries = useMemo(() => deriveSiteSummaries(company, reports), [company, reports]);
  const hasSites = siteSummaries.length > 0;
  const siteNameMap = useMemo(() => {
    return new Map(siteSummaries.map((site) => [site.id, site.name]));
  }, [siteSummaries]);
  const handleReportsCountClick = useCallback(() => {
    const reportQuery = company?.name?.trim() || company?.code?.trim() || undefined;
    navigate({
      to: '/reports',
      search: { query: reportQuery },
    });
  }, [company?.code, company?.name, navigate]);

  const handleOpenReport = useCallback(
    (reportId: string) => {
      navigate({
        to: '/reports/$reportId',
        params: { reportId },
        search: {},
      });
    },
    [navigate]
  );

  const handleEditReport = useCallback(
    (reportId: string) => {
      navigate({
        to: '/reports/$reportId',
        params: { reportId },
        search: { mode: 'edit' },
      });
    },
    [navigate]
  );

  const sortedReports = useMemo(() => {
    return [...reports].sort((a, b) => {
      const aTime = parseDate(a.visitDate) ?? parseDate(a.updatedAt) ?? 0;
      const bTime = parseDate(b.visitDate) ?? parseDate(b.updatedAt) ?? 0;
      return bTime - aTime;
    });
  }, [reports]);

  const recentReports = useMemo(() => {
    const drafts = sortedReports.filter((report) => report.status === 'draft');
    const finalized = sortedReports.filter((report) => report.status === 'finalized');
    return [...drafts, ...finalized].slice(0, 5);
  }, [sortedReports]);

  const personnelGroups = useMemo<PersonnelGroups>(() => {
    if (!company) {
      return {
        company: [],
        siteGroups: [],
        unassigned: [],
      };
    }

    const grouped = groupPersonnelByScope(company.personnel ?? [], company.sites ?? []);

    const sortGroup = (list: PersonRecord[]) => sortPersonnelByPriority(list);

    const siteGroups = (company.sites ?? []).map((site) => {
      const match = grouped.siteGroups.find((entry) => entry.siteId === site.id);
      const { name: siteName } = resolveSiteDisplayLabel(company, site);
      return {
        siteId: site.id,
        siteName,
        sequenceNumber: site.sequenceNumber ?? 0,
        shorthand: formatSiteShorthand(company, site),
        personnel: sortGroup(match ? match.personnel : []),
      };
    });

    return {
      company: sortGroup(grouped.company),
      siteGroups,
      unassigned: sortGroup(grouped.unassigned),
    };
  }, [company]);

  const breadcrumbFactory = useCallback(
    (baseTrail: BreadcrumbItem[]) => {
      const defaultTrail = [...baseTrail];

      if (!params.companyId) {
        return defaultTrail;
      }

      if (!company) {
        return [...defaultTrail, { label: 'Azienda', href: `/companies/${params.companyId}` }];
      }

      return [...defaultTrail, { label: company.name, href: `/companies/${company.id}` }];
    },
    [company, params.companyId]
  );

  const handleCreateReport = useCallback(
    (siteId?: string) => {
      if (!company) return;
      if (!hasSites) {
        toast.error('Nessuno stabilimento disponibile per creare un report.');
        return;
      }
      navigate({
        to: '/reports/$reportId',
        params: { reportId: 'new' },
        search: {
          companyId: company.id,
          siteId,
          mode: 'edit',
        },
      });
    },
    [company, hasSites, navigate]
  );

  const handleSiteNavigate = useCallback(
    (siteId: string) => {
      if (!company) return;
      navigate({
        to: '/companies/$companyId/sites/$siteId',
        params: { companyId: company.id, siteId },
        search: { tab: undefined },
      });
    },
    [company, navigate]
  );

  const handlePlanSite = useCallback(
    (site: SiteSummary) => {
      setPlanningDialog({
        open: true,
        siteId: site.id,
        siteName: site.name,
        plannedVisitAt: site.plannedVisitAt,
      });
    },
    [setPlanningDialog]
  );

  const actionItems = useMemo<ActivityItem[]>(() => {
    if (!hasSites) {
      return [
        {
          id: 'add-site',
          title: 'Nessuno stabilimento presente',
          icon: 'add_business',
          actionLabel: 'Aggiungi stabilimento',
          onClick: () => setNewSiteOpen(true),
        },
      ];
    }

    // Convert database activities to ActionItem format
    return activities.map((activity) => {
      const isOverdue = activity.dueDate && getDateStatus(activity.dueDate) === 'expired';
      const dueLabel = activity.dueDate ? formatDate(activity.dueDate) : undefined;
      const siteName = activity.siteId ? siteNameMap.get(activity.siteId) : undefined;

      // Determine icon based on category
      let icon: IconName = 'task_alt';
      if (activity.category === 'inspection-reminder') {
        icon = 'event';
      } else if (activity.category === 'cpi-renewal') {
        icon = 'local_fire_department';
      } else if (activity.category === 'authorization') {
        icon = 'verified';
      } else if (activity.category === 'certification') {
        icon = 'badge';
      } else if (activity.category === 'manual') {
        icon = activity.reportId ? 'edit_document' : 'task_alt';
      }

      return {
        id: activity.id,
        title: activity.title,
        subtitle: siteName
          ? `${siteName}${dueLabel ? ` · Scad. ${dueLabel}` : ''}`
          : dueLabel
            ? `Scad. ${dueLabel}`
            : undefined,
        icon,
        actionLabel: activity.status === 'pending' ? 'Avvia' : 'Completa',
        onClick: () => {
          if (activity.status === 'pending') {
            startActivityMutation.mutate(activity.id);
          } else {
            completeActivityMutation.mutate(activity.id);
          }
        },
        overdue: isOverdue,
      };
    });
  }, [
    activities,
    completeActivityMutation,
    hasSites,
    setNewSiteOpen,
    siteNameMap,
    startActivityMutation,
  ]);

  const handlePersonnelSelect = useCallback(
    (personnelId: string) => {
      const personnel = company?.personnel?.find((p) => p.id === personnelId);
      if (personnel) {
        setSelectedPersonnel(personnel);
        setPersonnelDialogOpen(true);
      }
    },
    [company?.personnel, setPersonnelDialogOpen]
  );

  const deleteCompanyMutation = useMutation({
    mutationFn: async () => {
      if (!company) throw new Error('Azienda non disponibile');
      await companies.deleteCompany(company.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.companies.all,
      });
      toast.success('Azienda archiviata');
      navigate({ to: '/companies' });
    },
  });

  const handleDeleteCompany = useCallback(async () => {
    await deleteCompanyMutation.mutateAsync();
  }, [deleteCompanyMutation]);

  useBreadcrumbEffect(breadcrumbFactory);

  if (isLoading) {
    return <LoadingState label="Caricamento scheda azienda" />;
  }

  if (!company) {
    return <></>;
  }

  const operativitaTabContent = (
    <CompanyOverviewTab
      actionItems={actionItems}
      onCreateReport={() => handleCreateReport(undefined)}
      reports={recentReports}
      siteNameMap={siteNameMap}
      hasSites={hasSites}
      onReportsClick={handleReportsCountClick}
      onOpenReport={handleOpenReport}
      onEditReport={handleEditReport}
    >
      <CompanySitesTab
        sectionId="company-sites"
        siteSummaries={siteSummaries}
        onCreateSite={() => setNewSiteOpen(true)}
        onNavigateSite={handleSiteNavigate}
        onPlanSite={handlePlanSite}
        onCreateReport={handleCreateReport}
      />
    </CompanyOverviewTab>
  );

  const anagraficaTabContent = (
    <>
      <CompanyRegistrySection company={company} onEditCompany={() => setEditCompanyOpen(true)} />
      <CompanyPersonnelTab
        personnelGroups={personnelGroups}
        onCreatePersonnel={() => setNewPersonnelOpen(true)}
        onEditPersonnel={handlePersonnelSelect}
      />
    </>
  );

  const tabContent = currentTab === 'anagrafica' ? anagraficaTabContent : operativitaTabContent;

  return (
    <PageLayout>
      <PageHeader title={company.name} icon="corporate_fare">
        <CompanyLogoMark logoUrl={company.logoUrl} size="lg" />
      </PageHeader>

      <TabNavigation
        activeTab={currentTab}
        onSelect={(tabId) => handleTabSelect(tabId as CompanyTab)}
        tabs={[
          { id: 'operativita', label: 'Operatività' },
          { id: 'anagrafica', label: 'Anagrafica' },
        ]}
      />

      <PageContent>{tabContent}</PageContent>

      <EditCompanyModal
        open={editCompanyOpen}
        onOpenChange={setEditCompanyOpen}
        company={company}
        onDelete={handleDeleteCompany}
        isDeleting={deleteCompanyMutation.isPending}
      />

      <NewSiteModal
        companyId={company.id}
        open={newSiteOpen}
        onOpenChange={setNewSiteOpen}
        onCreated={(siteId) => {
          setNewSiteOpen(false);
          navigate({
            to: '/companies/$companyId/sites/$siteId',
            params: { companyId: company.id, siteId },
            search: { tab: undefined },
          });
        }}
      />
      {personnelModalElement}

      <NewPersonnelModal
        companyId={company.id}
        companyCode={company.code}
        sites={company.sites}
        open={newPersonnelOpen}
        onOpenChange={setNewPersonnelOpen}
      />

      {planningDialog.siteId && planningDialog.siteName ? (
        <PlanningDateDialog
          open={planningDialog.open}
          onOpenChange={(open) => setPlanningDialog((prev) => ({ ...prev, open }))}
          siteId={planningDialog.siteId}
          siteName={planningDialog.siteName}
          companyId={company.id}
          plannedVisitAt={planningDialog.plannedVisitAt}
        />
      ) : null}
    </PageLayout>
  );
}
